// API helpers for the new (Enjo-style) auth flow.
//
// All calls send credentials so the backend's httpOnly cookies are included.
// baseURL is configured globally in src/index.js; these are relative paths that
// resolve to the trooprbot backend (route /api/auth.* there in your gateway).
import axios from 'axios';

const withCreds = { withCredentials: true };

// trooprbot is reached through the gateway under the /bot/* prefix (the same
// convention the rest of the app already uses for bot endpoints). The new auth
// routes live on trooprbot, so they are addressed as /bot/api/auth.*
const BOT = process.env.REACT_APP_BOT_PREFIX || '/bot';

// Slack OAuth initiation: env client id + redirect back to /slack_auth, which
// posts the code to the backend (slackUserLogin).
const SLACK_CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const APP_URI =
  process.env.REACT_APP_URI ||
  (typeof window !== 'undefined' ? window.location.origin : '');

// Resolve the trooprbot backend base for full-page OAuth redirects. axios
// baseURL is configured globally in index.js; reuse it so redirects hit the
// same gateway origin the XHR calls do.
const apiBase = () => `${axios.defaults.baseURL || ''}`.replace(/\/$/, '');

// POST email -> backend emails a 6-char code, returns { success, sessionId }
export const requestEmailOtp = (email, { isSignup } = {}) =>
  axios.post(`${BOT}/api/auth.email`, { email, isSignup: !!isSignup }, withCreds);

// POST sessionId + otp -> verifies, sets cookies, returns { success, user, isNewUser, isSignup }
export const verifyEmailOtp = ({ sessionId, otp, referrer }) =>
  axios.post(
    `${BOT}/api/verifyemailWithOtp`,
    { sessionId, otp, referrer },
    withCreds
  );

// POST slack code -> sets cookies, returns { success, user }
export const slackUserLogin = ({ code, redirectUri }) =>
  axios.post(
    `${BOT}/api/auth.slack.user.login`,
    { code, redirectUri },
    withCreds
  );

// "Who am I" via the access cookie -> { success, user }
export const fetchMe = () => axios.get(`${BOT}/api/auth/me`, withCreds);

// Rotate tokens via the refresh cookie -> { success }
export const refreshSession = () =>
  axios.post(`${BOT}/api/auth/refresh`, {}, withCreds);

// Full-page redirects for OAuth providers (go straight to the backend).
export const goToGoogleLogin = () => {
  window.location.href = `${apiBase()}${BOT}/api/auth/google.login`;
};
export const goToGoogleSignup = () => {
  window.location.href = `${apiBase()}${BOT}/api/auth/google.signup`;
};
export const goToSlackLogin = () => {
  // redirect_uri must be a URL registered in the Slack app AND must match the
  // redirect_uri used in the token exchange. REACT_APP_URI is that registered
  // callback (https://app-stage.troopr.io/slack_login). The SlackAccessAuth
  // page it lands on now posts the code to the COOKIE endpoint (slackUserLogin
  // → /bot/api/auth.slack.user.login) instead of the legacy Bearer
  // /auth/slack/access — see SignInPage/SlackAccess.js.
  const redirectUri = process.env.REACT_APP_URI;
  const state = JSON.stringify({ isLogin: true });
  window.location.href =
    'https://slack.com/oauth/v2/authorize' +
    `?client_id=${SLACK_CLIENT_ID}` +
    '&user_scope=openid' +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&state=${encodeURIComponent(state)}`;
};

export const logout = () => axios.post(`${BOT}/api/auth/logout`, {}, withCreds);
